import { useEffect, useRef } from 'react';
import { Modal, ModalBody, ModalFooter, Button, Text, useModal } from '@customerio/pluma-components/react';
import type { PlumaManagedModalProps } from '@customerio/pluma-components/react';

export type ReleaseNotificationModalData = {
  /** Reloads the page to pick up the new release. */
  onReload: () => void;
};

export function ReleaseNotificationModal({ modalState, data }: PlumaManagedModalProps<ReleaseNotificationModalData>) {
  return (
    <Modal state={modalState} isDismissable={false} title="New Release Ready!">
      <ModalBody>
        <Text>
          We're now ready to load the new version, and will bring you right back to this page in a few seconds.
        </Text>
      </ModalBody>
      <ModalFooter>
        <Button variant="primary" onClick={data.onReload}>
          Reload
        </Button>
      </ModalFooter>
    </Modal>
  );
}

type UseReleaseNotificationModalOptions = {
  /** Opens the modal when this becomes true. */
  isReleaseReady: boolean;
  /** Reloads the page to pick up the new release. */
  onReload: () => void;
};

/**
 * Opens the (non-dismissable) release notification modal once a new release is
 * ready. State-driven rather than click-driven: `useReleaseNotification` polls
 * for version bumps, so the modal is opened from an effect when the flag flips.
 */
export function useReleaseNotificationModal({ isReleaseReady, onReload }: UseReleaseNotificationModalOptions) {
  const releaseModal = useModal(ReleaseNotificationModal);
  // The modal is only ever shown once per page lifetime (accepting it reloads
  // the document); the ref also guards StrictMode's double-invoked effects.
  const hasOpenedRef = useRef(false);

  useEffect(() => {
    if (!isReleaseReady || hasOpenedRef.current) {
      return;
    }
    hasOpenedRef.current = true;
    // `shouldCloseOnUnmount` closes the modal if the authenticated layout
    // unmounts while it's open (e.g. a logout redirect to /login), so this
    // non-dismissable modal doesn't linger over the wrong shell.
    releaseModal.open({ onReload }, { shouldCloseOnUnmount: true });
  }, [isReleaseReady, onReload, releaseModal]);
}
